/**
 * Photos Tab Component
 * Photo viewer and management for the project workspace
 */

import { useEffect, useState, useMemo } from 'react';
import { Image, Search, Grid, List, MapPin, X, AlertTriangle } from 'lucide-react';
import { PhotoViewer } from '../../Photo/PhotoViewer';
import { useUIStore } from '../../../store/uiStore';
import { usePhotoStore, type Photo } from '../../../store/photoStore';

interface PhotosTabProps {
  projectId: string;
}

type ViewMode = 'grid' | 'list';
type CameraFilter = 'ALL' | 'FRONTE' | 'RETRO';

export function PhotosTab({ projectId }: PhotosTabProps) {
  const { 
    photos, 
    isLoading, 
    loadPhotosFromServer, 
    preloadThumbnails,
    setCurrentProject 
  } = usePhotoStore();
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [cameraFilter, setCameraFilter] = useState<CameraFilter>('ALL');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const loadData = async () => {
      setCurrentProject(projectId);
      await loadPhotosFromServer(projectId);
      
      // Preload thumbnails for better UX
      const photoIds = photos.map(p => p.id);
      if (photoIds.length > 0) {
        preloadThumbnails(photoIds);
      }
    };
    
    loadData();
  }, [projectId, loadPhotosFromServer, setCurrentProject, preloadThumbnails]);

  // Filter photos
  const filteredPhotos = useMemo(() => {
    return photos.filter(photo => {
      // Camera filter
      if (cameraFilter !== 'ALL' && photo.camera !== cameraFilter) {
        return false;
      }
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return photo.filename.toLowerCase().includes(query);
      }
      return true;
    });
  }, [photos, cameraFilter, searchQuery]);

  // Count by camera
  const cameraCounts = useMemo(() => {
    return {
      ALL: photos.length,
      FRONTE: photos.filter(p => p.camera === 'FRONTE').length,
      RETRO: photos.filter(p => p.camera === 'RETRO').length,
    };
  }, [photos]);

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (photos.length === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center bg-gray-50 p-8">
        <Image className="w-16 h-16 text-gray-300 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          No photos
        </h3>
        <p className="text-gray-500 text-center max-w-md">
          This project does not contain photos. Upload a ZIP archive 
          with project photos.
        </p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Toolbar */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-4">
        {/* Search */}
        <div className="flex-1 relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search photos..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Camera filter */}
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
          {(['ALL', 'FRONTE', 'RETRO'] as CameraFilter[]).map((cam) => (
            <button
              key={cam}
              onClick={() => setCameraFilter(cam)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                cameraFilter === cam
                  ? 'bg-white shadow text-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {cam === 'ALL' ? 'All' : cam}
              <span className="ml-1 text-xs text-gray-400">
                ({cameraCounts[cam]})
              </span>
            </button>
          ))}
        </div>

        {/* View mode toggle */}
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 rounded-md transition-colors ${
              viewMode === 'grid'
                ? 'bg-white shadow text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            title="Grid view"
          >
            <Grid className="w-4 h-4" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 rounded-md transition-colors ${
              viewMode === 'list'
                ? 'bg-white shadow text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            title="List view"
          >
            <List className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Photos grid/list */}
      <div className="flex-1 overflow-auto p-4">
        {filteredPhotos.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            No photos found with current filters
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-2">
            {filteredPhotos.map((photo) => (
              <button
                key={photo.id}
                onClick={() => setSelectedPhoto(photo)}
                className={`aspect-square rounded-lg overflow-hidden bg-gray-100 hover:ring-2 hover:ring-blue-400 transition-all relative group ${
                  selectedPhoto?.id === photo.id ? 'ring-2 ring-blue-600' : ''
                }`}
              >
                {photo.thumbnailUrl ? (
                  <img
                    src={photo.thumbnailUrl}
                    alt={photo.filename}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Image className="w-8 h-8 text-gray-300" />
                  </div>
                )}
                
                {/* Camera badge */}
                <span className={`absolute top-1 left-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                  photo.camera === 'FRONTE' 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-amber-500 text-white'
                }`}>
                  {photo.camera}
                </span>
                
                {/* GPS indicator */}
                {photo.gps && (
                  <MapPin className="absolute top-1 right-1 w-3 h-3 text-green-500" />
                )}
                
                {/* Filename on hover */}
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <p className="text-[10px] text-white truncate">{photo.filename}</p>
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Photo</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Filename</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-gray-500">Camera</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-gray-500">GPS</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-gray-500">Dimensions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredPhotos.map((photo) => (
                  <tr
                    key={photo.id}
                    onClick={() => setSelectedPhoto(photo)}
                    className={`hover:bg-gray-50 cursor-pointer transition-colors ${
                      selectedPhoto?.id === photo.id ? 'bg-blue-50' : ''
                    }`}
                  >
                    <td className="px-4 py-2">
                      <div className="w-12 h-12 rounded overflow-hidden bg-gray-100">
                        {photo.thumbnailUrl ? (
                          <img
                            src={photo.thumbnailUrl}
                            alt={photo.filename}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Image className="w-6 h-6 text-gray-300" />
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <span className="text-sm text-gray-900">{photo.filename}</span>
                    </td>
                    <td className="px-4 py-2 text-center">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        photo.camera === 'FRONTE'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-amber-100 text-amber-700'
                      }`}>
                        {photo.camera}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-center">
                      {photo.gps ? (
                        <MapPin className="w-4 h-4 text-green-500 mx-auto" />
                      ) : (
                        <span className="text-gray-300">-</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-center text-sm text-gray-500">
                      {photo.width && photo.height ? `${photo.width}×${photo.height}` : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Photo count */}
      <div className="bg-white border-t border-gray-200 px-4 py-2 text-sm text-gray-500">
        Showing {filteredPhotos.length} of {photos.length} photos
      </div>

      {/* Photo Viewer Overlay */}
      {selectedPhoto && (
        <div className="fixed inset-0 z-50 bg-black">
          {/* Close button */}
          <button
            onClick={() => setSelectedPhoto(null)}
            className="absolute top-4 right-4 z-50 p-3 bg-white/10 hover:bg-white/20 rounded-lg transition-colors backdrop-blur-sm"
          >
            <X className="w-6 h-6 text-white" />
          </button>

          {/* PhotoViewer Component */}
          <PhotoViewerWrapper 
            photo={selectedPhoto}
            onPhotoChange={(newPhoto) => setSelectedPhoto(newPhoto)}
          />
        </div>
      )}
    </div>
  );
}

/**
 * PhotoViewer Wrapper - loads photo and displays PhotoViewer
 */
interface PhotoViewerWrapperProps {
  photo: Photo;
  onPhotoChange: (photo: Photo) => void;
}

function PhotoViewerWrapper({ photo }: PhotoViewerWrapperProps) {
  const { setCurrentPhoto, selectElement } = useUIStore();
  const { getPhotoUrl } = usePhotoStore();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadAndSetPhoto = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Load full-size photo URL from server
        const fullUrl = await getPhotoUrl(photo.id, false); // false = full size
        
        if (!fullUrl) {
          throw new Error('Failed to load photo URL from server');
        }

        // Fetch blob to create File object
        const response = await fetch(fullUrl);
        const blob = await response.blob();
        const file = new File([blob], photo.filename, { type: blob.type });

        // Set current photo in UIStore
        setCurrentPhoto({
          id: photo.id,
          projectId: photo.projectId,
          file,
          dataUrl: fullUrl,
          width: photo.width,
          height: photo.height,
        });

        // Select element if photo is bound to one
        if (photo.elementId && photo.elementType) {
          console.log(`📌 Auto-selecting element: ${photo.elementType} #${photo.elementId} for photo ${photo.filename}`);
          // elementId is UUID string, not a number!
          selectElement(photo.elementId, photo.elementType);
        } else {
          console.warn(`⚠️ Photo ${photo.filename} is not bound to any element`);
        }
      } catch (err) {
        console.error('Failed to load photo:', err);
        setError(err instanceof Error ? err.message : 'Failed to load photo');
      } finally {
        setLoading(false);
      }
    };

    loadAndSetPhoto();
  }, [photo.id, photo.filename, photo.width, photo.height, photo.elementId, photo.elementType, getPhotoUrl, setCurrentPhoto, selectElement]);

  if (loading) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-white/30 border-t-white rounded-full mx-auto mb-4" />
          <p className="text-white text-lg">Loading photo...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <p className="text-white text-lg mb-2">Failed to load photo</p>
          <p className="text-white/70 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  // Render full PhotoViewer component
  return <PhotoViewer />;
}
